import React, { useState } from 'react';
import {
  AppBar, Toolbar, Button, Box, Menu, MenuItem,
  Drawer, List, ListItem, ListItemText, Collapse,
  ListItemButton, IconButton, Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

// New Icons matching mockup
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/filmhook_logo.svg';
import { useTheme } from '../../ThemeContext';

const navItems = [
  { label: 'Home', path: '/', icon: <HomeRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'About', path: '/about', icon: <PersonOutlineRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'Features', path: '/features', icon: <GridViewRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'Terms & Condition', path: '/termsAndCondition', icon: <ArticleOutlinedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'Privacy Policy', path: '/privacyPolicyAll', icon: <SecurityOutlinedIcon sx={{ fontSize: '1.2rem' }} /> },
  {
    label: 'Pages',
    icon: <LayersOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
    children: [
      { label: 'Blog', path: '/blog' },
      { label: "FAQ's", path: '/faq' },
      { label: 'Age Suitability', path: '/age-suitability' },
      { label: 'Child Safety', path: '/child-safety' },
      { label: 'Data Safety', path: '/data-safety' },
      { label: 'Account Deletion', path: '/account-deletion' },
      { label: 'Manage Account', path: '/manage-account' },
      { label: 'Marketing Page', path: '/marketing' },
      { label: 'Terms of Service', path: '/terms-of-service' },
    ]
  },
  { label: 'Contact Us', path: '/contact', icon: <PhoneOutlinedIcon sx={{ fontSize: '1.2rem' }} /> }
];

// Reusable clip-path for chamfered corners
const chamferedClipPath = 'polygon(16px 0%, calc(100% - 16px) 0%, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0% calc(100% - 16px), 0% 16px)';
const chamferedButtonClipPath = 'polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px)';

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenuLabel, setOpenSubMenuLabel] = useState(null);
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();

  const toggleDrawer = (open) => () => setMobileOpen(open);

  const handleMenuOpen = (event, label) => {
    setAnchorEl(event.currentTarget);
    setHoveredMenu(label);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setHoveredMenu(null);
  };

  const toggleMobileSubMenu = (label) => {
    setOpenSubMenuLabel(prev => (prev === label ? null : label));
  };

  const isActive = (path) => location.pathname === path;

  // Vertical Separator Component
  const VerticalSeparator = () => (
    <Box sx={{ 
      width: '1px', 
      height: '32px', 
      background: isDarkMode 
        ? 'linear-gradient(to bottom, transparent, rgba(7, 164, 245, 0.4), transparent)' 
        : 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1), transparent)',
      mx: 2
    }} />
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'transparent',
        boxShadow: 'none',
        mt: 3,
        mx: 'auto',
        maxWidth: '100%',
        width: 'calc(100% - 32px)',
        zIndex: 1100,
        // The drop-shadow is applied to the wrapper so it follows the clip-path of the child
        filter: isDarkMode 
          ? 'drop-shadow(0 0 15px rgba(7, 164, 245, 0.3)) drop-shadow(0 8px 20px rgba(0,0,0,0.6))' 
          : 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.08))',
      }}
    >
      {/* Sci-fi Chamfered Container */}
      <Box sx={{
        position: 'relative',
        background: isDarkMode ? '#050a14' : '#ffffff',
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
        clipPath: chamferedClipPath,
        // Inner glowing border effect using pseudo-element inside the clipped area
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(7,164,245,0.4) 0%, transparent 20%, transparent 80%, rgba(7,164,245,0.4) 100%)' 
            : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.05) 100%)',
          zIndex: -1,
          padding: '1px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          clipPath: chamferedClipPath,
        },
        // Top and Bottom center tech accents
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: '120px',
          height: '2px',
          background: isDarkMode ? '#07a4f5' : '#3b82f6',
          boxShadow: isDarkMode ? '0 0 10px #07a4f5' : 'none',
        }
      }}>
        {/* Bottom accent */}
        <Box sx={{
           position: 'absolute',
           bottom: 0, left: '50%',
           transform: 'translateX(-50%)',
           width: '120px',
           height: '3px',
           background: isDarkMode ? '#07a4f5' : '#e2e8f0',
           display: 'flex',
           justifyContent: 'center',
           alignItems: 'center',
           gap: '4px',
           clipPath: 'polygon(10px 0, calc(100% - 10px) 0, 100% 100%, 0 100%)'
        }}>
           {/* Tech dots/lines inside bottom accent */}
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
        </Box>

        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '80px', px: { xs: 2, md: 1.5 } }}>
          
          {/* Logo */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={logo} alt="Filmhook" style={{ height: '40px', filter: isDarkMode ? 'drop-shadow(0 0 8px rgba(255,255,255,0.15))' : 'none' }} />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2 }}>
            <VerticalSeparator />
            
            {navItems.map((item) =>
              item.children ? (
                <Box
                  key={item.label}
                  onMouseEnter={(e) => handleMenuOpen(e, item.label)}
                  onMouseLeave={handleMenuClose}
                  sx={{ position: 'relative' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      height: '70px',
                      minWidth: '80px',
                      px: 2,
                      color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        color: isDarkMode ? '#fff' : '#0f172a',
                      },
                    }}
                  >
                    {item.icon}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mt: 0.5 }}>{item.label}</Typography>
                      <ExpandMore sx={{ fontSize: '1rem', ml: 0.2 }} />
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={hoveredMenu === item.label}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                    PaperProps={{
                      sx: {
                        background: isDarkMode ? '#0a0f1a' : '#ffffff',
                        backdropFilter: 'blur(16px)',
                        border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.3)' : '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '0px',
                        clipPath: chamferedButtonClipPath,
                        mt: 1.5,
                      }
                    }}
                  >
                    {item.children.map((child) => (
                      <MenuItem
                        key={child.label}
                        component={Link}
                        to={child.path}
                        onClick={handleMenuClose}
                        sx={{
                          color: isActive(child.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.85)' : '#1e293b'),
                          fontWeight: isActive(child.path) ? 700 : 500,
                          fontSize: '0.85rem',
                          py: 1,
                          px: 3,
                          '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                        }}
                      >
                        {child.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ) : (
                <Box
                  key={item.label}
                  component={Link}
                  to={item.path}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    gap: 0.5,
                    height: '70px',
                    minWidth: '80px',
                    px: 2,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    // Active State Styling (Chamfered Glowing Box)
                    ...(isActive(item.path) ? {
                      color: '#07a4f5',
                      clipPath: chamferedButtonClipPath,
                      background: isDarkMode ? 'rgba(7, 164, 245, 0.05)' : '#f8fafc',
                      // Inner glow and border for active item
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: isDarkMode ? '#07a4f5' : '#e2e8f0',
                        zIndex: -1,
                        padding: '1px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      },
                      // The glowing bottom dash
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '12px',
                        width: '16px',
                        height: '2px',
                        background: '#07a4f5',
                        boxShadow: isDarkMode ? '0 0 8px #07a4f5' : 'none',
                      }
                    } : {
                      color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569',
                      '&:hover': {
                        color: isDarkMode ? '#fff' : '#0f172a',
                      }
                    })
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive(item.path) ? 700 : 600, mt: 0.5 }}>
                    {item.label}
                  </Typography>
                </Box>
              )
            )}
            
            <VerticalSeparator />
          </Box>

          {/* Right Side Tools */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            
            {/* Target-style Light/Dark Mode Switcher */}
            <Box sx={{
               position: 'relative',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '44px',
               height: '44px',
               '&::before': {
                 content: '""',
                 position: 'absolute',
                 top: 0, left: 0, right: 0, bottom: 0,
                 border: isDarkMode ? '1px dashed rgba(255,255,255,0.2)' : '1px dashed rgba(0,0,0,0.2)',
                 borderRadius: '50%',
                 animation: 'spin 10s linear infinite',
               }
            }}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  width: '32px',
                  height: '32px',
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: 'transparent',
                  '&:hover': {
                    color: isDarkMode ? '#07a4f5' : '#0f172a',
                  }
                }}
              >
                {isDarkMode ? <LightModeIcon sx={{ fontSize: '1.1rem' }} /> : <DarkModeIcon sx={{ fontSize: '1.1rem' }} />}
              </IconButton>
              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            </Box>

            {/* Chamfered Download App Button */}
            <Box sx={{ filter: isDarkMode ? 'drop-shadow(0 4px 12px rgba(7,164,245,0.4))' : 'drop-shadow(0 4px 10px rgba(0,0,0,0.1))' }}>
              <Button
                href="https://play.google.com/store/apps/details?id=com.filmhookapps&hl=en_IN"
                startIcon={<DownloadIcon />}
                target="_blank"
                sx={{
                  background: '#07a4f5',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  clipPath: chamferedButtonClipPath,
                  borderRadius: 0, // Border radius is handled by clip-path
                  px: 3,
                  py: 1.2,
                  '&:hover': {
                    background: '#0284c7',
                  },
                }}
              >
                Download App
              </Button>
            </Box>
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            sx={{ display: { xs: 'block', lg: 'none' }, color: isDarkMode ? 'white' : '#0d1530' }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Drawer */}
          <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)}>
            <Box
              sx={{
                width: 270,
                p: 2,
                background: isDarkMode ? '#050a14' : '#ffffff',
                minHeight: '100vh',
                borderLeft: isDarkMode ? '1px solid rgba(7, 164, 245,0.4)' : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pl: 1 }}>
                <img src={logo} alt="Filmhook" style={{ height: '40px' }} />
              </Box>
              <List>
                {navItems.map((item) =>
                  item.children ? (
                    <Box key={item.label}>
                      <ListItemButton
                        onClick={() => toggleMobileSubMenu(item.label)}
                        sx={{ borderRadius: '12px', mb: 0.5 }}
                      >
                        <Box sx={{ mr: 2, color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569', display: 'flex' }}>
                          {item.icon}
                        </Box>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ sx: { color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#0f172a', fontWeight: 600 } }}
                        />
                        {openSubMenuLabel === item.label
                          ? <ExpandLess sx={{ color: '#07a4f5' }} />
                          : <ExpandMore sx={{ color: 'rgba(255,255,255,0.3)' }} />}
                      </ListItemButton>
                      <Collapse in={openSubMenuLabel === item.label} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => (
                            <ListItem
                              button
                              key={child.label}
                              component={Link}
                              to={child.path}
                              onClick={toggleDrawer(false)}
                              sx={{
                                pl: 6,
                                borderRadius: '12px',
                                color: isActive(child.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569'),
                                '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                              }}
                            >
                              <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive(child.path) ? 700 : 500 }} />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  ) : (
                    <ListItem
                      button
                      key={item.label}
                      component={Link}
                      to={item.path}
                      onClick={toggleDrawer(false)}
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        color: isActive(item.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.85)' : '#0f172a'),
                        clipPath: isActive(item.path) ? chamferedButtonClipPath : 'none',
                        background: isActive(item.path) ? (isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)') : 'transparent',
                        '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                      }}
                    >
                      <Box sx={{ mr: 2, color: 'inherit', display: 'flex' }}>
                        {item.icon}
                      </Box>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive(item.path) ? 700 : 600 }} />
                    </ListItem>
                  )
                )}

                {/* Mobile Mode Switcher */}
                <ListItem sx={{ mt: 2, px: 2 }}>
                   <Button 
                     fullWidth
                     variant="outlined" 
                     onClick={toggleTheme}
                     startIcon={isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                     sx={{ 
                       clipPath: chamferedButtonClipPath,
                       borderRadius: 0, 
                       textTransform: 'none',
                       borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                       color: isDarkMode ? '#fff' : '#0f172a',
                       py: 1
                     }}
                   >
                     {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                   </Button>
                </ListItem>

                {/* Download App (Mobile) */}
                <ListItem sx={{ mt: 1, px: 2 }}>
                  <Button
                    fullWidth
                    href="https://play.google.com/store/apps/details?id=com.filmhookapps&hl=en_IN"
                    target="_blank"
                    startIcon={<DownloadIcon />}
                    sx={{ 
                      clipPath: chamferedButtonClipPath,
                      borderRadius: 0, 
                      background: '#07a4f5', 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.5,
                    }}
                  >
                    Download App
                  </Button>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Box>
    </AppBar>
  );
};

export default Header;
